import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import MedicalDisclaimer from '../../components/MedicalDisclaimer';

// ============================================================
// Mobile hook
// ============================================================

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

// ============================================================
// Типы
// ============================================================

interface ChironPosition {
  sign: string;
  house: number;
  degree: number;
}

interface MedicalAstrologyResult {
  vulnerableAreas: string[];
  healthStrengths: string[];
  recommendations: string[];
  warnings: string[];
  aiInterpretation: string;
  sixthHouseSign?: string;
  chironPosition?: ChironPosition;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// ============================================================
// Константы
// ============================================================

const SIGN_NAMES_RU: Record<string, string> = {
  Aries: 'Овен',
  Taurus: 'Телец',
  Gemini: 'Близнецы',
  Cancer: 'Рак',
  Leo: 'Лев',
  Virgo: 'Дева',
  Libra: 'Весы',
  Scorpio: 'Скорпион',
  Sagittarius: 'Стрелец',
  Capricorn: 'Козерог',
  Aquarius: 'Водолей',
  Pisces: 'Рыбы',
};

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(13, 20, 40, 0.85)',
  cardBorder: 'rgba(212, 175, 55, 0.12)',
  cardShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  danger: '#FF5252',
  dangerBg: 'rgba(255, 82, 82, 0.1)',
  success: '#4CAF50',
  successBg: 'rgba(76, 175, 80, 0.1)',
  warningOrange: '#FF9800',
};

const goldAccentLine = (
  <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
);

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// ============================================================
// Компонент
// ============================================================

const MedicalForecastPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [result, setResult] = useState<MedicalAstrologyResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ApiResponse<MedicalAstrologyResult>>('/medical');
      if (res.data.success && res.data.data) {
        setResult(res.data.data);
      } else {
        setError(res.data.message || 'Не удалось загрузить анализ');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки анализа';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAnalysis();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.gold,
        fontSize: '18px',
        fontFamily: 'var(--font-sans)',
      }}>
        Войдите, чтобы увидеть медицинский анализ
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.gold,
        fontSize: '18px',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏥</div>
          <div>Анализируем показатели здоровья...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        background: COLORS.dangerBg,
        border: `1px solid ${COLORS.danger}`,
        borderRadius: '12px',
        color: COLORS.danger,
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Ошибка</div>
        <div>{error}</div>
        <button
          onClick={loadAnalysis}
          style={{
            marginTop: '16px',
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
          }}
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        textAlign: 'center',
        color: COLORS.textMuted,
        fontFamily: 'var(--font-sans)',
      }}>
        Нет данных для анализа
      </div>
    );
  }

  const sixthSignRu = result.sixthHouseSign
    ? SIGN_NAMES_RU[result.sixthHouseSign] || result.sixthHouseSign
    : null;

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '16px 12px' : '24px 16px',
      fontFamily: 'var(--font-sans)',
      color: COLORS.text,
      position: 'relative',
    }}>
      {goldAccentLine}

      {/* Заголовок */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '32px' }}>
        <h1 style={{
          ...headingStyle,
          fontSize: isMobile ? '24px' : '32px',
          margin: '0 0 8px 0',
        }}>
          🏥 Медицинская астрология
        </h1>
        <p style={{ color: COLORS.textMuted, fontSize: isMobile ? '13px' : '15px', margin: 0 }}>
          Анализ уязвимых зон и сильных сторон здоровья по натальной карте
        </p>
        {sixthSignRu && (
          <p style={{ color: COLORS.goldLight, fontSize: '14px', marginTop: '8px' }}>
            6-й дом: {sixthSignRu}
            {result.chironPosition && ` • Хирон: {SIGN_NAMES_RU[result.chironPosition.sign] || result.chironPosition.sign} ({result.chironPosition.house} дом)`}
          </p>
        )}
      </div>

      {/* ДИСКЛЕЙМЕР — обязательно вверху */}
      <MedicalDisclaimer />

      {/* Уязвимые места (красные карточки) */}
      {result.vulnerableAreas.length > 0 && (
        <div style={{
          background: COLORS.dangerBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.danger}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            ⚠️ Уязвимые места
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {result.vulnerableAreas.map((area, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255, 82, 82, 0.08)',
                  border: `1px solid ${COLORS.danger}33`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              >
                {area}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Сильные стороны (зелёные карточки) */}
      {result.healthStrengths.length > 0 && (
        <div style={{
          background: COLORS.successBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.success}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            ✅ Сильные стороны здоровья
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
            {result.healthStrengths.map((strength, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(76, 175, 80, 0.08)',
                  border: `1px solid ${COLORS.success}33`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              >
                {strength}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI-интерпретация */}
      {result.aiInterpretation && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            ✨ Интерпретация
          </h2>
          <div
            style={{
              color: COLORS.text,
              fontSize: '14px',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.aiInterpretation}
          </div>
        </div>
      )}

      {/* Предостережения */}
      {result.warnings.length > 0 && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.08)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.warningOrange}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h3 style={{
            ...headingStyle,
            fontSize: isMobile ? '16px' : '18px',
            margin: '0 0 12px 0',
          }}>
            🔶 Предостережения
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: COLORS.text, fontSize: '14px', lineHeight: 1.8 }}>
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Рекомендации */}
      {result.recommendations.length > 0 && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.08)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.success}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: isMobile ? '24px' : '32px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h3 style={{
            ...headingStyle,
            fontSize: isMobile ? '16px' : '18px',
            margin: '0 0 12px 0',
          }}>
            ✅ Рекомендации
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: COLORS.text, fontSize: '14px', lineHeight: 1.8 }}>
            {result.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Кнопка обновления */}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <button
          onClick={loadAnalysis}
          disabled={loading}
          style={{
            padding: isMobile ? '10px 20px' : '10px 28px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: isMobile ? '13px' : '14px',
            fontFamily: 'var(--font-sans)',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Загрузка...' : 'Обновить анализ'}
        </button>
      </div>

      {/* Повторный дисклеймер внизу */}
      <div style={{ marginTop: '32px' }}>
        <MedicalDisclaimer size="compact" />
      </div>
    </div>
  );
};

export default MedicalForecastPage;
